import React, { useState, useCallback, useEffect } from 'react';
import { adminService } from '../api/services';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Search, RefreshCw, AlertCircle, Shield, User, Calendar, Video, FileText, BookOpen, TrendingUp, LogIn, LogOut, UserPlus } from 'lucide-react';

const ACTION_META = {
  USER_LOGIN:                { icon: LogIn,       color: 'text-forest-600 bg-forest-50',   label: 'Login'              },
  USER_LOGOUT:               { icon: LogOut,      color: 'text-forest-400 bg-cream-50',    label: 'Logout'             },
  USER_REGISTERED:           { icon: UserPlus,    color: 'text-blue-600 bg-blue-50',       label: 'Registered'         },
  BOOKING_CREATED:           { icon: Calendar,    color: 'text-amber-600 bg-amber-50',     label: 'Booking Created'    },
  BOOKING_CANCELLED:         { icon: Calendar,    color: 'text-rust-500 bg-rust-50',       label: 'Booking Cancelled'  },
  SESSION_STARTED:           { icon: Video,       color: 'text-green-600 bg-green-50',     label: 'Session Started'    },
  SESSION_ENDED:             { icon: Video,       color: 'text-forest-500 bg-forest-50',   label: 'Session Ended'      },
  SESSION_COMPLETED:         { icon: Video,       color: 'text-forest-700 bg-forest-50',   label: 'Session Completed'  },
  EXPERT_APPROVED:           { icon: Shield,      color: 'text-forest-700 bg-forest-50',   label: 'Expert Approved'    },
  EXPERT_REJECTED:           { icon: Shield,      color: 'text-rust-500 bg-rust-50',       label: 'Expert Rejected'    },
  CLIENT_PROMOTED_TO_EXPERT: { icon: TrendingUp,  color: 'text-purple-600 bg-purple-50',   label: 'Promoted to Expert' },
  CANVAS_SUBMITTED:          { icon: BookOpen,    color: 'text-blue-600 bg-blue-50',       label: 'Canvas Submitted'   },
  CANVAS_GRADED:             { icon: BookOpen,    color: 'text-forest-600 bg-forest-50',   label: 'Canvas Graded'      },
  DOCUMENT_UPLOADED:         { icon: FileText,    color: 'text-amber-600 bg-amber-50',     label: 'Doc Uploaded'       },
  DOCUMENT_DELETED:          { icon: FileText,    color: 'text-rust-500 bg-rust-50',       label: 'Doc Deleted'        },
  LEVEL_CREATED:             { icon: TrendingUp,  color: 'text-blue-600 bg-blue-50',       label: 'Level Created'      },
  LEVEL_UPDATED:             { icon: TrendingUp,  color: 'text-amber-600 bg-amber-50',     label: 'Level Updated'      },
  PAYMENT_RUN_CREATED:       { icon: FileText,    color: 'text-forest-600 bg-forest-50',   label: 'Payment Run'        },
};

const DEFAULT_META = { icon: Shield, color: 'text-forest-400 bg-cream-100', label: '' };

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return new Date(date).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminAuditLogPage = () => {
  const [logs,     setLogs]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [page,     setPage]     = useState(1);
  const [search,   setSearch]   = useState('');
  const [action,   setAction]   = useState('');
  const [expanded, setExpanded] = useState(null);
  const LIMIT = 30;

  const fetchLogs = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { page, limit: LIMIT };
      if (action) params.action = action;
      if (search) params.userId = search;
      const res  = await adminService.getAuditLogs(params);
      const body = res.data;
      // ApiResponse.paginated: { data: [...logs], meta: { total, totalPages } }
      const rows = Array.isArray(body.data) ? body.data : [];
      setLogs(rows);
      setTotal(body.meta?.total ?? rows.length);
    } catch (err) {
      setError('Could not load audit logs');
    } finally { setLoading(false); }
  }, [page, action, search]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <DashboardLayout title="Audit Log">
      <div className="space-y-5 max-w-5xl">

        <p className="text-sm text-forest-500">Complete record of all system activity.</p>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <select value={action} onChange={e => { setAction(e.target.value); setPage(1); }}
            className="px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500 bg-white text-forest-700">
            <option value="">All Actions</option>
            {Object.entries(ACTION_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button onClick={() => { setPage(1); fetchLogs(); }}
            className="p-2.5 rounded-xl border border-cream-200 text-forest-500 hover:bg-cream-50 transition-colors">
            <RefreshCw size={15} />
          </button>
          <span className="text-sm text-forest-400 self-center ml-auto">
            {loading ? 'Loading...' : `${total} events`}
          </span>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-12 gap-3 px-5 py-3 bg-cream-50 border-b border-cream-100 text-[10px] font-semibold text-forest-500 uppercase tracking-widest">
            <div className="col-span-3">Action</div>
            <div className="col-span-2">Entity</div>
            <div className="col-span-3">User</div>
            <div className="col-span-2">Time</div>
            <div className="col-span-2">Details</div>
          </div>

          {loading ? (
            [...Array(8)].map((_, i) => (
              <div key={i} className="h-14 px-5 flex items-center gap-3 animate-pulse border-b border-cream-50">
                <div className="w-7 h-7 rounded-full bg-cream-200 flex-shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-cream-200 rounded w-1/3" />
                  <div className="h-2.5 bg-cream-100 rounded w-1/4" />
                </div>
              </div>
            ))
          ) : logs.length === 0 ? (
            <div className="text-center py-16">
              <Shield size={32} className="text-forest-200 mx-auto mb-3" />
              <p className="font-serif text-xl text-forest-600">No activity yet</p>
            </div>
          ) : (
            logs.map(log => {
              const meta  = ACTION_META[log.action] ?? DEFAULT_META;
              const Icon  = meta.icon;
              const isExp = expanded === log.id;

              let newVal = null;
              try { newVal = log.newValue ? JSON.parse(log.newValue) : null; } catch {}

              return (
                <div key={log.id} className="border-b border-cream-50 last:border-0">
                  <div
                    className="grid grid-cols-12 gap-3 px-5 py-3.5 items-center hover:bg-cream-50/50 transition-colors cursor-pointer"
                    onClick={() => setExpanded(isExp ? null : log.id)}
                  >
                    <div className="col-span-3 flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                        <Icon size={12} />
                      </div>
                      <span className="text-xs font-medium text-forest-800 truncate">
                        {meta.label || log.action.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-forest-600 truncate">{log.entityType}</p>
                      <p className="text-[10px] text-forest-400 truncate font-mono">{log.entityId?.slice(0, 8)}…</p>
                    </div>
                    <div className="col-span-3">
                      <p className="text-xs text-forest-600 truncate font-mono">{log.userId?.slice(0, 8)}…</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-forest-500">{timeAgo(log.createdAt)}</p>
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="text-[10px] text-forest-400">{isExp ? '▲ hide' : '▼ show'}</span>
                    </div>
                  </div>

                  {isExp && (
                    <div className="px-5 pb-4 pt-2 bg-cream-50 border-t border-cream-100 text-xs space-y-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-1">Entity ID</p>
                          <p className="font-mono text-forest-700 break-all">{log.entityId}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-1">User ID</p>
                          <p className="font-mono text-forest-700 break-all">{log.userId}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-1">Timestamp</p>
                          <p className="text-forest-700">{new Date(log.createdAt).toLocaleString('en-GH')}</p>
                        </div>
                        {log.ipAddress && (
                          <div>
                            <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-1">IP Address</p>
                            <p className="font-mono text-forest-700">{log.ipAddress}</p>
                          </div>
                        )}
                      </div>
                      {newVal && (
                        <div>
                          <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-1">Details</p>
                          <pre className="bg-white border border-cream-200 rounded-lg p-3 text-[10px] text-forest-700 overflow-x-auto">
                            {JSON.stringify(newVal, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-3 py-2">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
            className="px-4 py-2 text-sm rounded-full border border-cream-200 text-forest-600 hover:bg-cream-50 disabled:opacity-40 transition-colors">
            ← Prev
          </button>
          <span className="text-sm text-forest-500">Page {page} of {totalPages || 1} · {total} events</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            className="px-4 py-2 text-sm rounded-full border border-cream-200 text-forest-600 hover:bg-cream-50 disabled:opacity-40 transition-colors">
            Next →
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminAuditLogPage;